import {
  Text,
  InputSelectControlField,
  Badge,
  Skeleton,
  GreyGrey70,
  Primary12Regular,
  IconBuildings,
  Primary13Regular,
  Box,
  Flex
} from '@sequencehq/core-components'
import { useState, FC, ReactNode, useMemo } from 'react'
import { useLoadAllItemsQuery } from '@sequencehq/api/utils'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { useQuery } from '@sequencehq/api'
import { HOURS, SECONDS } from '@sequencehq/utils/dates'

interface Props
  extends Omit<
    React.ComponentProps<typeof InputSelectControlField>,
    | 'options'
    | 'onChange'
    | 'placeholder'
    | 'initialValue'
    | 'createLabel'
    | 'onCreate'
  > {
  onChange: (aliasId: string) => void
  value?: string
}

export const CustomerAliasSelect: FC<Props> = ({
  onChange,
  value,
  ...selectControlProps
}) => {
  const aliasesInfiniteQuery = useLoadAllItemsQuery(
    dashboard20240730Client.getCustomerAliases,
    { limit: 100 },
    { staleTime: 1 * HOURS }
  )

  const aliases = useMemo(
    () => aliasesInfiniteQuery.data?.pages.flatMap(page => page.items) ?? [],
    [aliasesInfiniteQuery.data]
  )

  const [query, setQuery] = useState('')

  const filteredAliases = aliases.filter(alias => {
    return alias.value.toLowerCase().includes(query.toLowerCase())
  })

  return (
    <InputSelectControlField
      {...selectControlProps}
      options={filteredAliases.map(alias => ({
        label: alias.value,
        value: alias.id,
        icon: (
          <Flex alignItems="center" gap="4px">
            <Badge
              sentiment="neutral"
              textTransform="none"
              size="sm"
              containerStyle={{ maxWidth: '180px' }}
              textStyle={Primary12Regular}
            >
              <Box height="16px" width="16px">
                <IconBuildings color={GreyGrey70} />
              </Box>
              <CustomerNameLabel customerId={alias.customerId} />
            </Badge>
          </Flex>
        ) as ReactNode
      }))}
      onChange={onChange}
      placeholder="Find a customer alias"
      initialValue={value}
      createLabel={undefined}
      leadingIcon={undefined}
      onCreate={undefined}
      onSearch={setQuery}
    />
  )
}

const CustomerNameLabel: FC<{ customerId: string }> = ({ customerId }) => {
  const { data: customer, isPending } = useQuery(
    dashboard20240730Client.getCustomer,
    { id: customerId },
    { enabled: !!customerId, staleTime: 20 * SECONDS }
  )

  if (isPending) {
    return <Skeleton height="16px" width="80px" />
  }

  return (
    <Text
      overflow="hidden"
      textOverflow="ellipsis"
      whiteSpace="nowrap"
      {...Primary13Regular}
    >
      {customer?.legalName ?? ''}
    </Text>
  )
}
